// =============================================================================
// Module Project - UI: TaskAttachmentsSection
// Gestion des pièces jointes pour une tâche
// =============================================================================

import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useAttachmentsStore } from '../hooks/useAttachmentsStore';
import { getDownloadUrl, downloadFileAsBlob, downloadFileAsRawBlob, sanitizeFileName } from '../data/attachmentRepository';
import type { TaskAttachment } from '../types';
import { Button } from '@/components/ui/button';

import { Progress } from '@/components/ui/progress';
import { 
  Paperclip, 
  Upload, 
  Download, 
  Trash2, 
  File,
  FileImage,
  FileText,
  FileArchive,
  FileAudio,
  FileVideo,
  Loader2,
  Eye,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Lazy load PdfViewer for better performance
const PdfViewer = lazy(() => import('./PdfViewer').then(m => ({ default: m.PdfViewer })));

interface TaskAttachmentsSectionProps {
  taskId: string;
  projectId: string;
  orgId: string;
  userId?: string;
  disabled?: boolean;
  canDelete?: boolean;
}

const FILE_ICONS: Record<string, React.ReactNode> = {
  image: <FileImage className="h-4 w-4 text-green-500" />,
  pdf: <FileText className="h-4 w-4 text-red-500" />,
  text: <FileText className="h-4 w-4 text-blue-500" />,
  archive: <FileArchive className="h-4 w-4 text-yellow-500" />,
  audio: <FileAudio className="h-4 w-4 text-purple-500" />,
  video: <FileVideo className="h-4 w-4 text-pink-500" />,
  default: <File className="h-4 w-4 text-muted-foreground" />,
};

function getFileIcon(mimeType: string | null): React.ReactNode {
  if (!mimeType) return FILE_ICONS.default;
  if (mimeType.startsWith('image/')) return FILE_ICONS.image;
  if (mimeType === 'application/pdf') return FILE_ICONS.pdf;
  if (mimeType.startsWith('text/')) return FILE_ICONS.text;
  if (mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('tar')) return FILE_ICONS.archive;
  if (mimeType.startsWith('audio/')) return FILE_ICONS.audio;
  if (mimeType.startsWith('video/')) return FILE_ICONS.video;
  return FILE_ICONS.default;
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function TaskAttachmentsSection({
  taskId,
  projectId,
  orgId,
  userId,
  disabled = false,
  canDelete = false,
}: TaskAttachmentsSectionProps) {
  
  const {
    attachments,
    uploading,
    loading,
    loadAttachments,
    upload,
    remove,
  } = useAttachmentsStore(orgId);

  const [dragging, setDragging] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<TaskAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewPdfBlob, setPreviewPdfBlob] = useState<Blob | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TaskAttachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track loaded task ID to avoid unnecessary reloads
  const loadedTaskIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (taskId && taskId !== loadedTaskIdRef.current) {
      loadedTaskIdRef.current = taskId;
      loadAttachments(taskId);
    }
  }, [taskId, loadAttachments]);


  // Clear ref on unmount
  useEffect(() => {
    return () => {
      loadedTaskIdRef.current = null;
    };
  }, []);

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    for (const file of Array.from(files)) {
      // Sanitize filename before upload
      const cleanName = sanitizeFileName(file.name);
      // Create new file with sanitized name
      const sanitizedFile = new window.File([file], cleanName, { type: file.type });
      await upload(projectId, taskId, sanitizedFile, userId);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDownload = async (attachment: TaskAttachment) => {
    setDownloading(attachment.id);
    try {
      // Download as blob and trigger real download
      const blob = await downloadFileAsRawBlob(attachment.storage_path);
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.file_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } finally {
      setDownloading(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await remove(deleteTarget.id, taskId, userId);
    setDeleteTarget(null);
  };

  const handlePreview = async (attachment: TaskAttachment) => {
    setPreviewAttachment(attachment);
    setPreviewLoading(true);
    setPreviewError(false);
    setPreviewUrl(null);
    setPreviewPdfBlob(null);
    
    try {
      // For PDFs, download as raw blob for react-pdf
      if (isPdf(attachment.mime_type) && attachment.storage_path) {
        const blob = await downloadFileAsRawBlob(attachment.storage_path);
        if (blob) {
          setPreviewPdfBlob(blob);
        } else {
          setPreviewError(true);
        }
      } else {
        // For images, try to get URL
        let url = attachment.url;
        
        // If no URL, try to get signed URL
        if (!url && attachment.storage_path) {
          url = await getDownloadUrl(attachment.storage_path);
        }
        
        // If still no URL, try to download as blob
        if (!url && attachment.storage_path) {
          url = await downloadFileAsBlob(attachment.storage_path);
        }
        
        if (url) {
          setPreviewUrl(url);
        } else {
          setPreviewError(true);
        }
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      setPreviewError(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewAttachment(null);
    // Revoke blob URL if it exists to free memory
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewPdfBlob(null);
    setPreviewError(false);
    setPreviewLoading(false);
  };

  const openInNewTab = async (attachment: TaskAttachment) => {
    let url = attachment.url;
    if (!url && attachment.storage_path) {
      url = await getDownloadUrl(attachment.storage_path);
    }
    // Fallback to blob download
    if (!url && attachment.storage_path) {
      url = await downloadFileAsBlob(attachment.storage_path);
    }
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handlePdfDownload = async () => {
    if (!previewAttachment) return;
    
    if (previewPdfBlob) {
      // Create download link from existing blob
      const url = URL.createObjectURL(previewPdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = previewAttachment.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      await openInNewTab(previewAttachment);
    }
  };

  const isImage = (mimeType: string | null) => mimeType?.startsWith('image/');
  const isPdf = (mimeType: string | null) => mimeType === 'application/pdf';
  const isPreviewable = (mimeType: string | null) => isImage(mimeType) || isPdf(mimeType);

  return (
    <div className="space-y-2">
          {/* Drop zone */}
          {!disabled && (
            <label
              htmlFor="task-attachment-file-input"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={cn(
                'border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors block',
                dragging ? 'border-primary bg-primary/5' : 'border-muted hover:border-muted-foreground/50',
                uploading && 'pointer-events-none opacity-50'
              )}
            >
              <input
                id="task-attachment-file-input"
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => {
                  handleFileSelect(e.target.files);
                  e.target.value = '';
                }}
                className="sr-only"
              />
              {uploading ? (
                <div className="space-y-1">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin text-primary" />
                  <p className="text-xs text-muted-foreground">Téléchargement en cours...</p>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs text-muted-foreground">
                    Glissez-déposez ou cliquez pour ajouter
                  </p>
                </div>
              )}
            </label>
          )}

          {/* Attachments list */}
          {loading ? (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : attachments.length === 0 ? null : (
            <div className="space-y-2">
              {attachments.map(attachment => (
            <div
              key={attachment.id}
              className="flex items-start gap-3 p-2 rounded-lg border bg-card hover:bg-muted/50 transition-colors group"
            >
              {/* Thumbnail for images */}
              {isImage(attachment.mime_type) && attachment.url ? (
                <img
                  src={attachment.url}
                  alt={attachment.file_name}
                  className="w-12 h-12 object-cover rounded border shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => window.open(attachment.url, '_blank')}
                />
              ) : isPdf(attachment.mime_type) && attachment.url ? (
                <div 
                  className="w-12 h-12 flex items-center justify-center bg-red-50 dark:bg-red-950/30 rounded border shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => window.open(attachment.url, '_blank')}
                  title="Cliquez pour visualiser le PDF"
                >
                  <FileText className="h-5 w-5 text-red-500" />
                </div>
              ) : (
                <div className="w-12 h-12 flex items-center justify-center bg-muted rounded border shrink-0">
                  {getFileIcon(attachment.mime_type)}
                </div>
              )}

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{attachment.file_name}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{formatFileSize(attachment.file_size)}</span>
                  <span>•</span>
                  <span>{format(new Date(attachment.created_at), 'd MMM yyyy', { locale: fr })}</span>
                </div>
              </div>

              {/* Actions - toujours visibles */}
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-foreground hover:text-foreground"
                  onClick={() => handleDownload(attachment)}
                  disabled={downloading === attachment.id}
                  title="Télécharger"
                >
                  {downloading === attachment.id ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                </Button>
                {isPreviewable(attachment.mime_type) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-foreground hover:text-foreground"
                    onClick={() => handlePreview(attachment)}
                    title="Visualiser"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                )}
                {(canDelete || attachment.uploaded_by === userId) && !disabled && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-foreground hover:text-destructive"
                    onClick={() => setDeleteTarget(attachment)}
                    title="Supprimer"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewAttachment} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 pb-2 shrink-0">
            <DialogTitle className="truncate pr-8">
              {previewAttachment?.file_name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto p-4 pt-0 min-h-0">
            {previewLoading && (
              <div className="flex items-center justify-center h-[300px]">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}
            {previewError && previewAttachment && (
              <div className="flex flex-col items-center justify-center h-[300px] gap-4">
                <p className="text-muted-foreground text-center">
                  Impossible de charger la prévisualisation.
                </p>
                <Button onClick={() => openInNewTab(previewAttachment)}>
                  Ouvrir dans un nouvel onglet
                </Button>
              </div>
            )}
            {!previewLoading && !previewError && previewUrl && previewAttachment && isImage(previewAttachment.mime_type) && (
              <img
                src={previewUrl}
                alt={previewAttachment.file_name}
                className="max-w-full max-h-[70vh] mx-auto object-contain rounded-lg"
              />
            )}
            {!previewLoading && !previewError && previewPdfBlob && previewAttachment && isPdf(previewAttachment.mime_type) && (
              <Suspense fallback={
                <div className="flex items-center justify-center h-[400px]">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              }>
                <PdfViewer 
                  pdfData={previewPdfBlob}
                  fileName={previewAttachment.file_name}
                />
              </Suspense>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette pièce jointe ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le fichier "{deleteTarget?.file_name}" sera définitivement supprimé. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
